// src/components/partner/AddExistingLearnerModal.jsx
import {
  useEffect,
  useState,
} from "react";
import {
  Mail,
  UserPlus,
  X,
} from "lucide-react";
import { addExistingLearnerToCohort } from "../../lib/partner/partnerLearners";

export default function AddExistingLearnerModal({
  open,
  cohortId,
  cohortName,
  onClose,
  onAdded,
}) {
  const [email, setEmail] =
    useState("");

  const [saving, setSaving] =
    useState(false);

  const [error, setError] =
    useState("");

  useEffect(() => {
    if (!open) {
      setEmail("");
      setSaving(false);
      setError("");

      return undefined;
    }

    const handleEscape = (event) => {
      if (
        event.key === "Escape" &&
        !saving
      ) {
        onClose?.();
      }
    };

    window.addEventListener(
      "keydown",
      handleEscape
    );

    return () => {
      window.removeEventListener(
        "keydown",
        handleEscape
      );
    };
  }, [
    open,
    saving,
    onClose,
  ]);

  if (!open) return null;

  function handleBackdropClick(event) {
    if (
      event.target ===
        event.currentTarget &&
      !saving
    ) {
      onClose?.();
    }
  }

  async function handleSubmit(event) {
    event.preventDefault();

    try {
      setSaving(true);
      setError("");

      await addExistingLearnerToCohort({
        cohortId,
        email,
      });

      await onAdded?.();

      onClose?.();
    } catch (submitError) {
      console.error(
        "Adding learner failed:",
        submitError
      );

      setError(
        submitError?.message ||
          "The learner could not be added."
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      role="presentation"
      onMouseDown={
        handleBackdropClick
      }
      className="fixed inset-0 z-[100] flex items-end justify-center bg-black/45 p-0 sm:items-center sm:p-6"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-learner-title"
        className="w-full rounded-t-[28px] bg-white shadow-2xl sm:max-w-lg sm:rounded-[28px]"
      >
        <header className="flex items-start justify-between border-b border-[#EFEFEF] px-6 py-5">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.14em] text-[#FF914D]">
              {cohortName ||
                "Partner cohort"}
            </p>

            <h2
              id="add-learner-title"
              className="mt-1 text-2xl font-black text-[#333333]"
            >
              Add an existing learner
            </h2>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            aria-label="Close add learner form"
            className="flex h-10 w-10 items-center justify-center rounded-full text-[#757575] transition hover:bg-[#FFF4EC] hover:text-[#FF914D] disabled:opacity-50"
          >
            <X className="h-5 w-5" />
          </button>
        </header>

        <form
          onSubmit={handleSubmit}
          className="space-y-5 p-6"
        >
          {error && (
            <div
              role="alert"
              className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700"
            >
              {error}
            </div>
          )}

          <div className="rounded-2xl border border-[#FFE1CF] bg-[#FFF7ED] p-4 text-sm leading-relaxed text-[#75523B]">
            The learner must already have a registered
            Gwin account. Enter the same email address
            used for that account.
          </div>

          <div>
            <label
              htmlFor="existing-learner-email"
              className="block text-sm font-black text-[#333333]"
            >
              Learner email address
            </label>

            <div className="relative mt-2">
              <Mail className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#999999]" />

              <input
                id="existing-learner-email"
                type="email"
                value={email}
                onChange={(event) =>
                  setEmail(
                    event.target.value
                  )
                }
                disabled={saving}
                required
                autoComplete="email"
                placeholder="learner@example.com"
                className="w-full rounded-2xl border border-[#DDDDDD] py-3 pl-11 pr-4 text-sm outline-none transition focus:border-[#FF914D] focus:ring-4 focus:ring-[#FF914D]/15"
              />
            </div>
          </div>

          <div className="flex flex-col-reverse gap-3 border-t border-[#EFEFEF] pt-5 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="rounded-full border border-[#DDDDDD] px-5 py-3 text-sm font-black text-[#555555] transition hover:bg-[#F7F7F7] disabled:opacity-50"
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center justify-center rounded-full bg-[#FF914D] px-6 py-3 text-sm font-black text-white transition hover:bg-[#E9782F] disabled:cursor-not-allowed disabled:opacity-60"
            >
              <UserPlus className="mr-2 h-4 w-4" />

              {saving
                ? "Adding learner…"
                : "Add learner"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}